import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Download, Smartphone, Monitor, Share2, LogIn, ShieldCheck, CheckCircle2 } from "lucide-react";
import PageHeader from "../components/PageHeader";
import { ADMIN_BASE } from "../config";

const STEPS = [
    {
        icon: Smartphone,
        device: "iPhone / iPad (Safari)",
        steps: ["Tap the Share button (the square with an up-arrow).", 'Scroll and tap "Add to Home Screen".', 'Tap "Add" — the app icon appears on your home screen.'],
    },
    {
        icon: Smartphone,
        device: "Android (Chrome)",
        steps: ['Tap the "Download & Install App" button above, or', "Tap the ⋮ menu (top-right) in Chrome.", 'Tap "Install app" / "Add to Home screen".'],
    },
    {
        icon: Monitor,
        device: "Computer (Chrome / Edge)",
        steps: ['Click the "Download & Install App" button above, or', "Click the install icon ⊕ in the address bar.", 'Click "Install" — it opens in its own window.'],
    },
];

export default function AppAccess() {
    const [prompt, setPrompt] = useState(null);
    const [installed, setInstalled] = useState(false);
    const [showSteps, setShowSteps] = useState(false);

    useEffect(() => {
        const onPrompt = (e) => {
            e.preventDefault();
            setPrompt(e);
        };
        const onInstalled = () => setInstalled(true);
        window.addEventListener("beforeinstallprompt", onPrompt);
        window.addEventListener("appinstalled", onInstalled);
        if (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) {
            setInstalled(true);
        }
        return () => {
            window.removeEventListener("beforeinstallprompt", onPrompt);
            window.removeEventListener("appinstalled", onInstalled);
        };
    }, []);

    const install = async () => {
        if (prompt) {
            prompt.prompt();
            try {
                await prompt.userChoice;
            } catch {
                /* dismissed */
            }
            setPrompt(null);
        } else {
            setShowSteps(true);
        }
    };

    return (
        <div data-testid="app-access-page" className="pb-28">
            <PageHeader
                testid="app-access-header"
                breadcrumb="Get the App"
                title="Install our app"
                number="11"
                label="Get the App"
                heading="Take NeuroPlay Counseling with you"
            >
                <div className="mt-10 grid gap-6 lg:grid-cols-2">
                    {/* Install card */}
                    <div className="rounded-3xl border border-amber-500/15 bg-[#1F1510]/50 p-8">
                        <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-amber-500/15 ring-1 ring-amber-500/30">
                            <Download className="h-6 w-6 text-amber-300" />
                        </span>
                        <h2 className="mt-5 font-serif text-2xl text-amber-50">Download &amp; install the app</h2>
                        <p className="mt-3 text-sm leading-relaxed text-amber-100/70">
                            Install NeuroPlay Counseling on your phone or computer for one-tap access — it opens
                            in its own window like a normal app and stays up to date automatically. No app
                            store needed.
                        </p>
                        <button
                            data-testid="install-app-button"
                            onClick={install}
                            disabled={installed}
                            className="mt-6 inline-flex items-center gap-2 rounded-full bg-amber-500 px-6 py-3 text-sm font-semibold text-[#1F1510] transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-amber-400 disabled:cursor-default disabled:opacity-70"
                        >
                            {installed ? (
                                <>
                                    <CheckCircle2 className="h-4 w-4" /> App installed
                                </>
                            ) : (
                                <>
                                    <Download className="h-4 w-4" /> Download &amp; Install App
                                </>
                            )}
                        </button>
                        <button
                            data-testid="install-steps-toggle"
                            onClick={() => setShowSteps((v) => !v)}
                            className="mt-4 block text-xs text-amber-300/80 underline hover:text-amber-200"
                        >
                            {showSteps ? "Hide step-by-step instructions" : "Show step-by-step instructions for my device"}
                        </button>

                        {showSteps && (
                            <div data-testid="install-steps" className="mt-5 space-y-4">
                                {STEPS.map((s) => (
                                    <div key={s.device} className="rounded-2xl border border-amber-500/10 bg-[#150e08]/60 p-4">
                                        <div className="flex items-center gap-2 text-sm font-semibold text-amber-100">
                                            <s.icon className="h-4 w-4 text-amber-400" /> {s.device}
                                        </div>
                                        <ol className="mt-2 list-decimal space-y-1 pl-5 text-xs leading-relaxed text-amber-100/65 marker:text-amber-400/70">
                                            {s.steps.map((step) => (
                                                <li key={step}>{step}</li>
                                            ))}
                                        </ol>
                                    </div>
                                ))}
                                <p className="flex items-start gap-2 text-xs text-amber-100/50">
                                    <Share2 className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                                    The one-tap install button appears automatically on supported browsers over a secure (https) connection.
                                </p>
                            </div>
                        )}
                    </div>

                    {/* Admin sign-in card */}
                    <div className="rounded-3xl border border-amber-500/15 bg-[#1F1510]/50 p-8">
                        <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-amber-500/15 ring-1 ring-amber-500/30">
                            <ShieldCheck className="h-6 w-6 text-amber-300" />
                        </span>
                        <h2 className="mt-5 font-serif text-2xl text-amber-50">Team &amp; admin sign-in</h2>
                        <p className="mt-3 text-sm leading-relaxed text-amber-100/70">
                            For Dora and approved team members only. Sign in securely with your Google
                            account to manage the website, blog, events, trainings, and posts. New team
                            members are granted access after an administrator approves them.
                        </p>
                        <Link
                            to={ADMIN_BASE}
                            data-testid="admin-signin-button"
                            className="mt-6 inline-flex items-center gap-2 rounded-full border border-amber-400/40 px-6 py-3 text-sm font-semibold text-amber-200 transition-colors duration-300 hover:bg-amber-500/10"
                        >
                            <LogIn className="h-4 w-4" /> Sign in to Admin
                        </Link>
                        <p className="mt-4 text-xs text-amber-100/45">
                            You can also install this control panel as an app from your device after signing in.
                        </p>
                    </div>
                </div>
            </PageHeader>
        </div>
    );
}
